<template>
    <div class="webphone-dialer">
        <!-- Call Status Display -->
        <div class="call-status-display">
            <div v-if="callState.status === 'idle'" class="idle-display">
                <div class="dial-number">
                    <input 
                        ref="numberInput"
                        v-model="localDialNumber"
                        @input="handleNumberInput"
                        @keydown="handleKeyDown"
                        class="number-input"
                        placeholder="Numara girin..."
                        type="tel"
                    />
                </div>
                <div class="sip-info">
                    <span class="extension-info">{{ sipStatus.extensionNumber }}@{{ sipStatus.domain }}</span>
                </div>
            </div>

            <div v-else-if="callState.status === 'calling'" class="calling-display">
                <div class="status-text">Aranıyor...</div>
                <div class="caller-number">{{ callState.callerNumber }}</div>
                <div class="call-timer">{{ formatDuration(callElapsed) }}</div>
            </div>

            <div v-else-if="callState.status === 'ringing'" class="ringing-display">
                <div class="status-text">Çalıyor...</div>
                <div class="caller-number">{{ callState.callerNumber }}</div>
                <div class="call-timer">{{ formatDuration(callElapsed) }}</div>
            </div>

            <div v-else-if="callState.status === 'incoming'" class="incoming-display">
                <div class="status-text">Gelen Arama</div>
                <div class="caller-info">
                    <div class="caller-avatar">
                        <i class="material-icons">person</i>
                    </div>
                    <div class="caller-details">
                        <div class="caller-name">{{ callState.callerName || 'Bilinmeyen' }}</div>
                        <div class="caller-number">{{ callState.callerNumber }}</div>
                    </div>
                </div>
            </div>

            <div v-else-if="callState.status === 'connected'" class="connected-display">
                <div class="status-text">
                    {{ callState.isOnHold ? 'Beklemede' : 'Konuşuyor' }}
                </div>
                <div class="caller-number">{{ callState.callerNumber }}</div>
                <div class="call-timer">{{ formatDuration(callState.callDuration) }}</div>
            </div>
        </div>

        <!-- Keypad -->
        <div class="keypad" v-if="shouldShowKeypad">
            <div class="keypad-row" v-for="(row, rowIndex) in keypadLayout" :key="rowIndex">
                <button 
                    v-for="key in row" 
                    :key="key.value"
                    class="keypad-key"
                    @click="handleKeyPress(key.value)"
                    :class="{ 'key-action': key.action }"
                >
                    <span class="key-primary">{{ key.primary }}</span>
                    <span v-if="key.secondary" class="key-secondary">{{ key.secondary }}</span>
                </button>
            </div>
        </div>

        <!-- Call Actions -->
        <div class="call-actions">
            <!-- Idle State Actions -->
            <div v-if="callState.status === 'idle'" class="idle-actions">
                <button 
                    class="action-btn clear-btn"
                    @click="clearNumber"
                    :disabled="!localDialNumber"
                    title="Numarayı temizle"
                >
                    <i class="material-icons">clear</i>
                </button>
                <button 
                    class="action-btn call-btn"
                    @click="$emit('call')"
                    :disabled="!localDialNumber.trim() || !sipStatus.isRegistered"
                    title="Aramayı başlat"
                >
                    <i class="material-icons">call</i>
                </button>
                <button 
                    class="action-btn delete-btn"
                    @click="deleteDigit"
                    :disabled="!localDialNumber"
                    title="Son rakamı sil"
                >
                    <i class="material-icons">backspace</i>
                </button>
            </div>

            <!-- Incoming Call Actions -->
            <div v-else-if="callState.status === 'incoming'" class="incoming-actions">
                <button class="action-btn answer-btn" @click="$emit('answer')">
                    <i class="material-icons">call</i>
                </button>
                <button class="action-btn reject-btn" @click="$emit('reject')">
                    <i class="material-icons">call_end</i>
                </button>
            </div>

            <!-- Calling/Ringing State Actions -->
            <div v-else-if="callState.status === 'calling' || callState.status === 'ringing'" class="calling-actions">
                <button class="action-btn hangup-btn" @click="hangup">
                    <i class="material-icons">call_end</i>
                </button>
            </div>

            <!-- Connected State Actions -->
            <div v-else-if="callState.status === 'connected'" class="connected-actions">
                <button 
                    class="action-btn mute-btn"
                    @click="$emit('mute')"
                    :class="{ active: callState.isMuted }"
                >
                    <i class="material-icons">{{ callState.isMuted ? 'mic_off' : 'mic' }}</i>
                </button>
                
                <button 
                    class="action-btn hold-btn"
                    @click="$emit('hold')"
                    :class="{ active: callState.isOnHold }"
                >
                    <i class="material-icons">{{ callState.isOnHold ? 'play_arrow' : 'pause' }}</i>
                </button>
                
                <button class="action-btn keypad-toggle-btn" @click="toggleKeypad">
                    <i class="material-icons">dialpad</i>
                </button>
                
                <button class="action-btn transfer-btn" @click="showTransfer" title="Yönlendir">
                    <i class="material-icons">call_split</i>
                </button>
                
                <button class="action-btn hangup-btn" @click="hangup">
                    <i class="material-icons">call_end</i>
                </button>
            </div>
        </div>

        <!-- Transfer Modal -->
        <div v-if="showTransferModal" class="transfer-modal" @click.self="cancelTransfer">
            <div class="transfer-content">
                <div class="transfer-header">
                    <h4>Aramayı Yönlendir</h4>
                    <button @click="cancelTransfer" class="close-btn">
                        <i class="material-icons">close</i>
                    </button>
                </div>
                
                <!-- Transfer Mode Tabs -->
                <div class="transfer-mode-tabs">
                    <button 
                        class="mode-tab"
                        :class="{ active: transferMode === 'extension' }"
                        @click="transferMode = 'extension'"
                    >
                        <i class="material-icons">people</i>
                        <span>Dahiliye Yönlendir</span>
                    </button>
                    <button 
                        class="mode-tab"
                        :class="{ active: transferMode === 'manual' }"
                        @click="transferMode = 'manual'"
                    >
                        <i class="material-icons">dialpad</i>
                        <span>Numaraya Yönlendir</span>
                    </button>
                </div>
                
                <!-- Extension Mode (Dahili Listesi) -->
                <div v-if="transferMode === 'extension'" class="extension-mode">
                <div class="search-box">
                    <i class="material-icons search-icon">search</i>
                    <input 
                        v-model="searchQuery"
                        class="search-input"
                        placeholder="Dahili ara..."
                    />
                </div>

                <div class="extensions-list">
                    <div v-if="filteredExtensions.length === 0" class="no-results">
                        <i class="material-icons">search_off</i>
                        <span>Dahili bulunamadı</span>
                    </div>
                    
                    <div 
                        v-for="extension in filteredExtensions"
                        :key="extension.extension"
                        class="extension-item"
                        :class="{ 
                            selected: selectedExtension === extension.extension,
                            offline: !extension.registeraddr,
                            busy: extension.isBusy,
                            disabled: !extension.isAvailable
                        }"
                        @click="selectExtension(extension)"
                    >
                        <div class="extension-avatar-wrapper">
                            <AgentAvatar
                                v-if="mapping[extension.extension]"
                                :userID="mapping[extension.extension]"
                                :size="32"
                                :isNamed="false"
                            />
                            <div
                                v-else
                                class="default-avatar"
                                :style="{ backgroundColor: getAvatarColor(extension.displayname) }"
                            >
                                <span>{{ getInitials(extension.displayname) }}</span>
                            </div>

                            <div 
                                class="status-indicator" 
                                :class="{
                                    'online': extension.registeraddr && !extension.isBusy,
                                    'busy': extension.registeraddr && extension.isBusy,
                                    'offline': !extension.registeraddr
                                }"
                            ></div>
                        </div>

                        <div class="extension-info">
                            <div class="extension-name">{{ extension.displayname }}</div>
                            <div class="extension-number">{{ extension.extension }}</div>
                            <div class="extension-status">
                                <i class="material-icons status-icon" 
                                   :class="{
                                       'online': extension.registeraddr && !extension.isBusy,
                                       'busy': extension.registeraddr && extension.isBusy,
                                       'offline': !extension.registeraddr
                                   }">
                                    {{ 
                                        !extension.registeraddr ? 'phone_disabled' : 
                                        extension.isBusy ? 'phone_in_talk' : 
                                        'phone'
                                    }}
                                </i>
                                <span>{{ 
                                    !extension.registeraddr ? 'Çevrimdışı' : 
                                    extension.isBusy ? 'Meşgul' : 
                                    'Müsait'
                                }}</span>
                            </div>
                        </div>

                        <div class="selection-indicator" v-if="selectedExtension === extension.extension">
                            <i class="material-icons">check_circle</i>
                        </div>
                        
                        <div class="disabled-overlay" v-if="!extension.isAvailable">
                            <i class="material-icons">block</i>
                        </div>
                    </div>
                </div>
                </div>
                
                <!-- Manual Mode (Numara Girişi) -->
                <div v-if="transferMode === 'manual'" class="manual-mode">
                    <div class="manual-input-container">
                        <div class="manual-input-label">
                            <i class="material-icons">phone</i>
                            <span>Yönlendirmek istediğiniz numarayı girin</span>
                        </div>
                        <input 
                            v-model="manualTransferNumber"
                            class="manual-number-input"
                            type="tel"
                            placeholder="05321234567"
                            @input="handleManualNumberInput"
                            @keydown.enter="confirmTransfer"
                        />
                        <div class="manual-input-hint">
                            <i class="material-icons">info</i>
                            <span>Telefon numarası girerken boşluk ve özel karakterler otomatik temizlenir</span>
                        </div>
                    </div>
                </div>
                
                <div class="transfer-actions">
                    <button @click="cancelTransfer" class="cancel-btn">İptal</button>
                    <button 
                        @click="confirmTransfer" 
                        class="confirm-btn"
                        :disabled="!isTransferValid"
                    >
                        Yönlendir
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Vue from "vue";
import AgentAvatar from "../../../../../components/common/AgentAvatar.vue";

export default Vue.extend({
    name: "WebPhoneDialer",
    components: {
        AgentAvatar,
    },
    props: {
        callState: {
            type: Object,
            required: true,
        },
        dialNumber: {
            type: String,
            default: "",
        },
        sipStatus: {
            type: Object,
            required: true,
        },
        extensions: {
            type: Array,
            default: () => [],
        },
        mapping: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            localDialNumber: this.dialNumber,
            showKeypadInCall: false,
            showTransferModal: false,
            selectedExtension: null,
            searchQuery: "",
            callElapsed: 0,
            elapsedTimer: null,
            manualTransferNumber: "", // Manuel numara girişi için
            transferMode: "extension", // "extension" veya "manual"
            keypadLayout: [
                [
                    { primary: "1", secondary: null, value: "1" },
                    { primary: "2", secondary: "ABC", value: "2" },
                    { primary: "3", secondary: "DEF", value: "3" },
                ],
                [
                    { primary: "4", secondary: "GHI", value: "4" },
                    { primary: "5", secondary: "JKL", value: "5" },
                    { primary: "6", secondary: "MNO", value: "6" },
                ],
                [
                    { primary: "7", secondary: "PQRS", value: "7" },
                    { primary: "8", secondary: "TUV", value: "8" },
                    { primary: "9", secondary: "WXYZ", value: "9" },
                ],
                [
                    { primary: "*", secondary: null, value: "*" },
                    { primary: "0", secondary: "+", value: "0" },
                    { primary: "#", secondary: null, value: "#" },
                ],
            ],
        };
    },
    computed: {
        shouldShowKeypad() {
            return this.callState.status === 'idle' || 
                   (this.callState.status === 'connected' && this.showKeypadInCall);
        },
        
        users() {
            return this.$store.state.user.users?.data || [];
        },
        
        netgsmHistory() {
            return this.$store.state.user.netgsmHistory || [];
        },
        
        isTransferValid() {
            if (this.transferMode === 'extension') {
                return !!this.selectedExtension;
            } else if (this.transferMode === 'manual') {
                return this.manualTransferNumber && this.manualTransferNumber.trim().length > 0;
            }
            return false;
        },
        
        filteredExtensions() {
            let filtered = this.extensions;
            
            // Kendi dahilimizi hariç tut
            if (this.sipStatus.extensionNumber) {
                filtered = filtered.filter(ext => ext.extension !== this.sipStatus.extensionNumber);
            }
            
            // Arama sorgusu varsa filtrele
            if (this.searchQuery) {
                const query = this.searchQuery.toLowerCase();
                filtered = filtered.filter(ext => 
                    ext.displayname.toLowerCase().includes(query) ||
                    ext.extension.toLowerCase().includes(query)
                );
            }
            
            // Her dahili için NetGSM history'den durumunu kontrol et
            filtered = filtered.map(ext => {
                const userId = this.mapping[ext.extension];
                const user = userId ? this.users.find(u => u._id === userId) : null;
                
                // NetGSM history'den dahili durumunu kontrol et
                const isBusyFromHistory = this.isExtensionBusyFromHistory(ext.extension);
                
                // Kullanıcı conversations'dan da kontrol et (fallback)
                const isBusyFromConversations = user ? this.isUserBusy(user) : false;
                
                // İkisinden biri meşgulse meşgul say
                const isBusy = isBusyFromHistory || isBusyFromConversations;
                
                return {
                    ...ext,
                    userId,
                    user,
                    isBusy,
                    isAvailable: ext.registeraddr && !isBusy
                };
            });
            
            // Sıralama: Müsait olanlar önce, sonra meşgul, sonra offline
            return filtered.sort((a, b) => {
                // 1. Müsait olanlar en üstte
                if (a.isAvailable && !b.isAvailable) return -1;
                if (!a.isAvailable && b.isAvailable) return 1;
                
                // 2. Online ama meşgul olanlar ortada
                if (a.registeraddr && a.isBusy && (!b.registeraddr || !b.isBusy)) return -1;
                if ((!a.registeraddr || !a.isBusy) && b.registeraddr && b.isBusy) return 1;
                
                // 3. Offline olanlar en altta
                if (a.registeraddr && !b.registeraddr) return -1;
                if (!a.registeraddr && b.registeraddr) return 1;
                
                // 4. Aynı durumdaysa isme göre sırala
                return a.displayname.localeCompare(b.displayname);
            });
        },
    },
    methods: {
        handleNumberInput(event) {
            const value = event.target.value;
            // Sadece rakamlar, +, *, # karakterlerine izin ver
            const sanitized = value.replace(/[^0-9+*#]/g, '');
            this.localDialNumber = sanitized;
            this.$emit('number-change', this.localDialNumber);
            
            // Input değeri sanitized versiyondan farklıysa güncelle
            if (value !== sanitized) {
                this.$nextTick(() => {
                    event.target.value = sanitized;
                });
            }
        },
        
        handleKeyDown(event) {
            // Backspace ve Delete tuşları için
            if (event.key === 'Backspace' || event.key === 'Delete') {
                // Normal davranışa izin ver, input otomatik olarak güncellenir
                return;
            }
            
            // Enter tuşu ile arama başlat (idle durumunda)
            if (event.key === 'Enter' && this.callState.status === 'idle') {
                event.preventDefault();
                if (this.localDialNumber.trim() && this.sipStatus.isRegistered) {
                    this.$emit('call');
                }
                return;
            }
            
            // Keypad karakterleri için DTMF gönder (eğer konuşma halindeyse)
            if (this.callState.status === 'connected') {
                const validKeys = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '#'];
                if (validKeys.includes(event.key)) {
                    this.$emit('dtmf', event.key);
                }
            }
            
            // Idle durumunda klavyeden girilen karakterleri input'a ekle
            if (this.callState.status === 'idle') {
                const validKeys = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '#'];
                if (validKeys.includes(event.key)) {
                    // Tuş sesini çal
                    this.$emit('play-dtmf-tone', event.key);
                    // Input'un kendi davranışına izin ver
                    return;
                }
            }
        },
        
        handleKeyPress(digit) {
            // Tuş sesini çal (parent'a bildir)
            this.$emit('play-dtmf-tone', digit);
            
            if (this.callState.status === 'idle') {
                this.localDialNumber += digit;
                this.$emit('number-change', this.localDialNumber);
            } else if (this.callState.status === 'connected') {
                this.$emit('dtmf', digit);
            }
        },
        
        deleteDigit() {
            if (this.localDialNumber.length > 0) {
                this.localDialNumber = this.localDialNumber.slice(0, -1);
                this.$emit('number-change', this.localDialNumber);
            }
        },
        
        clearNumber() {
            this.localDialNumber = "";
            this.$emit('number-change', this.localDialNumber);
        },
        
        toggleKeypad() {
            this.showKeypadInCall = !this.showKeypadInCall;
        },
        
        showTransfer() {
            this.showTransferModal = true;
            this.selectedExtension = null;
            this.searchQuery = "";
            this.manualTransferNumber = "";
            this.transferMode = "extension"; // Varsayılan olarak dahili modu
        },
        
        cancelTransfer() {
            this.showTransferModal = false;
            this.selectedExtension = null;
            this.searchQuery = "";
            this.manualTransferNumber = "";
            this.transferMode = "extension";
        },
        
        selectExtension(extension) {
            // Eğer dahili meşgul veya offline ise seçilemesin
            if (!extension.isAvailable) {
                if (extension.isBusy) {
                    this.$message.warning('Bu temsilci şu an aktif bir görüşmede, yönlendirme yapılamıyor!');
                } else if (!extension.registeraddr) {
                    this.$message.warning('Bu temsilci şu an çevrimdışı, yönlendirme yapılamıyor!');
                }
                return;
            }
            
            this.selectedExtension = extension.extension;
        },
        
        confirmTransfer() {
            // Manuel mod: Girilen numaraya yönlendir
            if (this.transferMode === 'manual') {
                if (!this.manualTransferNumber || !this.manualTransferNumber.trim()) {
                    this.$message.error('Lütfen bir numara girin!');
                    return;
                }
                
                // Numara zaten input'ta temizlendi ama son bir kontrol
                const cleanedNumber = this.manualTransferNumber.trim();
                
                if (!cleanedNumber) {
                    this.$message.error('Geçerli bir numara girin!');
                    return;
                }
                
                this.$emit('transfer', cleanedNumber);
                this.showTransferModal = false;
                this.selectedExtension = null;
                this.searchQuery = "";
                this.manualTransferNumber = "";
                return;
            }
            
            // Dahili mod: Seçili dahiliye yönlendir
            if (!this.selectedExtension) return;
            
            // Seçili dahilinin güncel durumunu kontrol et
            const extension = this.filteredExtensions.find(ext => ext.extension === this.selectedExtension);
            
            if (!extension) {
                this.$message.error('Dahili bulunamadı!');
                return;
            }
            
            if (!extension.isAvailable) {
                if (extension.isBusy) {
                    this.$message.error('Bu temsilci şu an aktif bir görüşmede, yönlendirme yapılamıyor!');
                } else {
                    this.$message.error('Bu temsilci şu an çevrimdışı, yönlendirme yapılamıyor!');
                }
                this.cancelTransfer();
                return;
            }
            
                this.$emit('transfer', this.selectedExtension);
                this.showTransferModal = false;
                this.selectedExtension = null;
                this.searchQuery = "";
            this.manualTransferNumber = "";
        },
        
        /**
         * Kullanıcının aktif konuşması olup olmadığını kontrol eder
         * @param {Object} user - Kullanıcı objesi
         * @returns {boolean} - Kullanıcı meşgulse true
         */
        isUserBusy(user) {
            if (!user || !user.conversations) return false;
            
            // Kullanıcının aktif konuşması var mı?
            const hasActiveConversation = user.conversations.some(convId => {
                const conversation = this.$store.state.chat.conversations[convId];
                if (!conversation) return false;
                
                // Aktif ve kullanıcıya atanmış konuşmalar
                return conversation.status === 'active' && 
                       conversation.agent === user._id;
            });
            
            return hasActiveConversation;
        },
        
        /**
         * Manuel numara girişini işler ve temizler
         * Kullanıcı her karakter girdiğinde veya yapıştırdığında çalışır
         */
        handleManualNumberInput(event) {
            const input = event.target.value;
            
            // Numarayı temizle: + 90 boşluk vb. tüm özel karakterleri kaldır
            const cleaned = this.cleanPhoneNumber(input);
            
            // Temizlenmiş numarayı input'a geri yaz
            this.manualTransferNumber = cleaned;
            
            // Input cursor pozisyonunu sona taşı
            this.$nextTick(() => {
                event.target.selectionStart = cleaned.length;
                event.target.selectionEnd = cleaned.length;
            });
        },
        
        /**
         * NetGSM history'den dahilinin durumunu kontrol eder
         * @param {string} extensionNumber - Dahili numarası (örn: "108", "110")
         * @returns {boolean} - Dahili meşgulse true
         */
        isExtensionBusyFromHistory(extensionNumber) {
            if (!this.netgsmHistory || this.netgsmHistory.length === 0) {
                return false;
            }
            
            // Bu dahiliye ait en güncel event'i bul
            // History array'i zaten en yeniden eskiye sıralı (App.vue'da)
            const latestEvent = this.netgsmHistory.find(event => {
                return event.data?.internal_num === extensionNumber;
            });
            
            if (!latestEvent || !latestEvent.data?.scenario) {
                return false;
            }
            
            const scenario = latestEvent.data.scenario;
            
            // Bu scenario'lar meşgul anlamına gelir
            const busyScenarios = ['Answer', 'Outbound_call', 'Inbound_call'];
            
            return busyScenarios.includes(scenario);
        },
        
        /**
         * Telefon numarasını temizler
         * Örnekler:
         * - "+90 552 342 20 02" → "05523422002"
         * - "90 552 342 20 02" → "05523422002"
         * - "0 552 342 20 02" → "05523422002"
         * - "552 342 20 02" → "05523422002"
         * 
         * @param {string} phoneNumber - Temizlenecek telefon numarası
         * @returns {string} - Temizlenmiş numara (sadece rakamlar, 0 ile başlar)
         */
        cleanPhoneNumber(phoneNumber) {
            if (!phoneNumber) return '';
            
            // 1. Sadece rakamları al (tüm özel karakterleri ve boşlukları kaldır)
            // + - ( ) boşluk vb. tüm karakterler kaldırılır
            let cleaned = phoneNumber.replace(/\D/g, '');
            
            // 2. Türk numarası kontrolü ve ülke kodu temizleme
            if (cleaned.startsWith('90')) {
                // 90 ile başlıyorsa (ülke kodu), kaldır
                cleaned = cleaned.substring(2);
            }
            
            // 3. 0 ile başlamıyorsa, başına 0 ekle (Türk numarası formatı)
            if (cleaned.length > 0 && !cleaned.startsWith('0')) {
                cleaned = '0' + cleaned;
            }
            
            return cleaned;
        },
        
        getInitials(name) {
            if (!name) return "?";
            
            const parts = name.trim().split(" ");
            if (parts.length === 1) {
                return parts[0].charAt(0).toUpperCase();
            } else {
                return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
            }
        },
        
        getAvatarColor(name) {
            const colors = [
                "#FF6B6B", "#4ECDC4", "#45B7D1", "#96CEB4", "#FECA57",
                "#FF9FF3", "#54A0FF", "#5F27CD", "#00D2D3", "#FF9F43",
                "#10AC84", "#EE5A24", "#0984E3", "#B71C1C", "#6C5CE7"
            ];

            if (!name) return colors[0];

            let hash = 0;
            for (let i = 0; i < name.length; i++) {
                hash = name.charCodeAt(i) + ((hash << 5) - hash);
            }

            return colors[Math.abs(hash) % colors.length];
        },
        
        formatDuration(seconds) {
            const hours = Math.floor(seconds / 3600);
            const minutes = Math.floor((seconds % 3600) / 60);
            const secs = seconds % 60;
            
            if (hours > 0) {
                return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
            } else {
                return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
            }
        },
        
        startElapsedTimer() {
            this.callElapsed = 0;
            this.elapsedTimer = setInterval(() => {
                this.callElapsed++;
            }, 1000);
        },
        
        stopElapsedTimer() {
            if (this.elapsedTimer) {
                clearInterval(this.elapsedTimer);
                this.elapsedTimer = null;
            }
            this.callElapsed = 0;
        },
        hangup() {
            console.log("hangup called from dialer", this.callState);
            this.$emit('hangup');
        },

        focusInput() {
            this.$nextTick(() => {
                if (this.$refs.numberInput) {
                    this.$refs.numberInput.focus();
                }
            });
        },
    },
    watch: {
        dialNumber(newVal) {
            this.localDialNumber = newVal;
        },
        
        'callState.status'(newStatus, oldStatus) {
            if (newStatus === 'calling' || newStatus === 'ringing') {
                this.startElapsedTimer();
            } else if (oldStatus === 'calling' || oldStatus === 'ringing') {
                this.stopElapsedTimer();
            }
            
            if (newStatus === 'idle') {
                // Numara alanını temizleme - kullanıcı yeniden numara girebilir
                this.showKeypadInCall = false;
                // Not: localDialNumber'ı temizlemiyoruz, kullanıcı aynı numarayı tekrar arayabilir
            }
        },
    },
    beforeDestroy() {
        this.stopElapsedTimer();
    },
});
</script>

<style scoped lang="scss">
.webphone-dialer {
    height: 100%;
    display: flex;
    flex-direction: column;
    padding: 15px;
    gap: 15px;
}

.call-status-display {
    flex: 0 0 auto;
    text-align: center;
    
    .idle-display {
        .dial-number {
            margin-bottom: 10px;
            
            .number-input {
                width: 100%;
                font-size: 24px;
                font-weight: 300;
                text-align: center;
                border: 1px solid transparent;
                border-radius: 8px;
                padding: 8px;
                background: transparent;
                color: #1C1C1E;
                outline: none;
                transition: border-color 0.2s ease;
                
                &:focus {
                    border-color: #007AFF;
                    background: rgba(0, 122, 255, 0.05);
                }
                
                &::placeholder {
                    color: #8E8E93;
                }
            }
        }
        
        .sip-info {
            .extension-info {
                font-size: 12px;
                color: #8E8E93;
            }
        }
    }
    
    .calling-display, .ringing-display, .connected-display {
        .status-text {
            font-size: 14px;
            color: #8E8E93;
            margin-bottom: 8px;
        }
        
        .caller-number {
            font-size: 20px;
            font-weight: 500;
            color: #1C1C1E;
            margin-bottom: 8px;
        }
        
        .call-timer {
            font-size: 16px;
            color: #007AFF;
            font-weight: 500;
        }
    }
    
    .incoming-display {
        .status-text {
            font-size: 14px;
            color: #8E8E93;
            margin-bottom: 15px;
        }
        
        .caller-info {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 12px;
            
            .caller-avatar {
                width: 50px;
                height: 50px;
                border-radius: 50%;
                background: #E5E5EA;
                display: flex;
                align-items: center;
                justify-content: center;
                
                .material-icons {
                    color: #8E8E93;
                    font-size: 24px;
                }
            }
            
            .caller-details {
                text-align: left;
                
                .caller-name {
                    font-size: 18px;
                    font-weight: 600;
                    color: #1C1C1E;
                    margin-bottom: 4px;
                }
                
                .caller-number {
                    font-size: 14px;
                    color: #8E8E93;
                }
            }
        }
    }
}

.keypad {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 12px;
    
    .keypad-row {
        display: flex;
        justify-content: center;
        gap: 12px;
        
        .keypad-key {
            width: 65px;
            height: 65px;
            border-radius: 50%;
            border: none;
            background: #F2F2F7;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            transition: all 0.15s ease;
            
            &:hover {
                background: #E5E5EA;
                transform: scale(1.05);
            }
            
            &:active {
                transform: scale(0.95);
                background: #D1D1D6;
            }
            
            .key-primary {
                font-size: 26px;
                font-weight: 300;
                color: #1C1C1E;
                line-height: 1;
            }
            
            .key-secondary {
                font-size: 9px;
                color: #8E8E93;
                font-weight: 500;
                letter-spacing: 0.5px;
                margin-top: -2px;
            }
        }
    }
}

.call-actions {
    flex: 0 0 auto;
    
    .idle-actions, .incoming-actions, .calling-actions, .connected-actions {
        display: flex;
        justify-content: center;
        gap: 12px;
        flex-wrap: wrap;
        
        .action-btn {
            width: 60px;
            height: 60px;
            border-radius: 50%;
            border: none;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            transition: all 0.2s ease;
            
            .material-icons {
                font-size: 28px;
                color: white;
            }
            
            &:hover {
                transform: scale(1.05);
            }
            
            &:active {
                transform: scale(0.95);
            }
            
            &:disabled {
                opacity: 0.5;
                cursor: not-allowed;
                transform: none;
            }
        }
        
        .call-btn {
            background: #34C759;
            box-shadow: 0 4px 15px rgba(52, 199, 89, 0.3);
            
            &:hover:not(:disabled) {
                background: #2FB653;
                box-shadow: 0 6px 20px rgba(52, 199, 89, 0.4);
            }
        }
        
        .answer-btn {
            background: #34C759;
            box-shadow: 0 4px 15px rgba(52, 199, 89, 0.3);
            
            &:hover {
                background: #2FB653;
            }
        }
        
        .reject-btn, .hangup-btn {
            background: #FF3B30;
            box-shadow: 0 4px 15px rgba(255, 59, 48, 0.3);
            
            &:hover {
                background: #E8322A;
            }
        }
        
        .delete-btn, .clear-btn {
            background: #8E8E93;
            
            &:hover:not(:disabled) {
                background: #6D6D70;
            }
        }
        
        .mute-btn, .hold-btn, .keypad-toggle-btn, .transfer-btn {
            background: #007AFF;
            
            &:hover {
                background: #0056CC;
            }
            
            &.active {
                background: #FF9500;
                
                &:hover {
                    background: #E6850E;
                }
            }
        }
    }
    
    .idle-actions {
        justify-content: space-between;
        max-width: 250px;
        margin: 0 auto;
        
        .action-btn {
            width: 55px;
            height: 55px;
            
            .material-icons {
                font-size: 26px;
            }
        }
        
        .call-btn {
            width: 70px;
            height: 70px;
            
            .material-icons {
                font-size: 32px;
            }
        }
    }
    
    .connected-actions {
        flex-wrap: wrap;
        max-width: 200px;
        margin: 0 auto;
        
        .action-btn {
            width: 50px;
            height: 50px;
            
            .material-icons {
                font-size: 24px;
            }
        }
    }
}

.transfer-modal {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10000;
    
    .transfer-content {
        background: white;
        border-radius: 12px;
        width: 90%;
        max-width: 400px;
        max-height: 80vh;
        overflow: hidden;
        display: flex;
        flex-direction: column;
        
        .transfer-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 20px;
            border-bottom: 1px solid #F2F2F7;
            
            h4 {
                margin: 0;
                color: #1C1C1E;
                font-size: 16px;
                font-weight: 600;
            }
            
            .close-btn {
                background: none;
                border: none;
                padding: 4px;
                border-radius: 4px;
                cursor: pointer;
                color: #8E8E93;
                
                &:hover {
                    background: rgba(0, 0, 0, 0.05);
                }
                
                .material-icons {
                    font-size: 20px;
                }
            }
        }
        
        .transfer-mode-tabs {
            display: flex;
            gap: 8px;
            padding: 15px 20px;
            border-bottom: 1px solid #F2F2F7;
            
            .mode-tab {
                flex: 1;
                display: flex;
                flex-direction: column;
                align-items: center;
                gap: 6px;
                padding: 12px 16px;
                border: 2px solid #E5E5EA;
                border-radius: 8px;
                background: white;
                cursor: pointer;
                transition: all 0.2s ease;
                
                .material-icons {
                    font-size: 24px;
                    color: #8E8E93;
                }
                
                span {
                    font-size: 12px;
                    font-weight: 500;
                    color: #8E8E93;
                }
                
                &:hover {
                    border-color: #007AFF;
                    background: rgba(0, 122, 255, 0.05);
                }
                
                &.active {
                    border-color: #007AFF;
                    background: rgba(0, 122, 255, 0.1);
                    
                    .material-icons,
                    span {
                        color: #007AFF;
                    }
                }
            }
        }
        
        .extension-mode {
            display: flex;
            flex-direction: column;
            max-height: 400px;
        }
        
        .manual-mode {
            padding: 30px 20px;
            min-height: 200px;
            
            .manual-input-container {
                display: flex;
                flex-direction: column;
                gap: 16px;
                
                .manual-input-label {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    color: #1C1C1E;
                    font-weight: 500;
                    
                    .material-icons {
                        color: #007AFF;
                        font-size: 20px;
                    }
                }
                
                .manual-number-input {
                    width: 100%;
                    padding: 14px 16px;
                    border: 2px solid #E5E5EA;
                    border-radius: 8px;
                    font-size: 16px;
                    font-weight: 500;
                    text-align: center;
                    transition: all 0.2s ease;
                    
                    &:focus {
                        outline: none;
                        border-color: #007AFF;
                        background: rgba(0, 122, 255, 0.05);
                    }
                    
                    &::placeholder {
                        color: #C7C7CC;
                        font-weight: 400;
                    }
                }
                
                .manual-input-hint {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    padding: 12px;
                    background: #F2F2F7;
                    border-radius: 8px;
                    font-size: 12px;
                    color: #8E8E93;
                    
                    .material-icons {
                        font-size: 16px;
                        color: #007AFF;
                    }
                }
            }
        }
        
        .search-box {
            position: relative;
            padding: 15px 20px;
            border-bottom: 1px solid #F2F2F7;
            
            .search-icon {
                position: absolute;
                left: 32px;
                top: 50%;
                transform: translateY(-50%);
                color: #8E8E93;
                font-size: 18px;
            }
            
            .search-input {
                width: 100%;
                padding: 10px 40px 10px 40px;
                border: 1px solid #D1D1D6;
                border-radius: 8px;
                font-size: 14px;
                background: white;
                
                &:focus {
                    outline: none;
                    border-color: #007AFF;
                }
                
                &::placeholder {
                    color: #8E8E93;
                }
            }
        }
        
        .extensions-list {
            flex: 1;
            overflow-y: auto;
            padding: 10px 0;
            
            .no-results {
                display: flex;
                flex-direction: column;
                align-items: center;
                padding: 40px 20px;
                color: #8E8E93;
                
                .material-icons {
                    font-size: 32px;
                    margin-bottom: 8px;
                    color: #C7C7CC;
                }
                
                span {
                    font-size: 14px;
                }
            }
            
            .extension-item {
                display: flex;
                align-items: center;
                padding: 12px 20px;
                cursor: pointer;
                transition: background-color 0.2s ease;
                position: relative;
                
                &:hover:not(.disabled) {
                    background: rgba(0, 122, 255, 0.05);
                }
                
                &.selected {
                    background: rgba(0, 122, 255, 0.1);
                }
                
                &.offline {
                    opacity: 0.6;
                }
                
                &.busy {
                    opacity: 0.8;
                }
                
                &.disabled {
                    cursor: not-allowed;
                    opacity: 0.5;
                    pointer-events: auto; // Yine de click event'i yakala (uyarı için)
                    
                    &:hover {
                        background: rgba(255, 59, 48, 0.05);
                    }
                }
                
                .extension-avatar-wrapper {
                    flex: 0 0 auto;
                    position: relative;
                    margin-right: 12px;
                    
                    .default-avatar {
                        width: 32px;
                        height: 32px;
                        border-radius: 50%;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        font-weight: 600;
                        color: white;
                        font-size: 12px;
                    }
                    
                    .status-indicator {
                        position: absolute;
                        bottom: 0;
                        right: 0;
                        width: 10px;
                        height: 10px;
                        border-radius: 50%;
                        border: 2px solid white;
                        
                        &.online {
                            background: #34C759; // Yeşil - Müsait
                        }
                        
                        &.busy {
                            background: #FF9500; // Turuncu - Meşgul
                        }
                        
                        &.offline {
                            background: #FF3B30; // Kırmızı - Offline
                        }
                    }
                }
                
                .extension-info {
                    flex: 1;
                    min-width: 0;
                    
                    .extension-name {
                        font-size: 14px;
                        font-weight: 500;
                        color: #1C1C1E;
                        margin-bottom: 2px;
                        white-space: nowrap;
                        overflow: hidden;
                        text-overflow: ellipsis;
                    }
                    
                    .extension-number {
                        font-size: 12px;
                        color: #8E8E93;
                        margin-bottom: 1px;
                    }
                    
                    .extension-status {
                        display: flex;
                        align-items: center;
                        gap: 4px;
                        font-size: 11px;
                        color: #8E8E93;
                        
                        .status-icon {
                            font-size: 11px;
                            
                            &.online {
                                color: #34C759;
                            }
                            
                            &.busy {
                                color: #FF9500;
                            }
                            
                            &.offline {
                                color: #FF3B30;
                            }
                        }
                        
                        span {
                            font-size: 11px;
                        }
                    }
                }
                
                .selection-indicator {
                    flex: 0 0 auto;
                    color: #007AFF;
                    
                    .material-icons {
                        font-size: 20px;
                    }
                }
                
                .disabled-overlay {
                    position: absolute;
                    top: 50%;
                    right: 20px;
                    transform: translateY(-50%);
                    color: #FF3B30;
                    opacity: 0.7;
                    
                    .material-icons {
                        font-size: 20px;
                    }
                }
            }
        }
        
        .transfer-actions {
            display: flex;
            gap: 12px;
            padding: 20px;
            border-top: 1px solid #F2F2F7;
            
            button {
                flex: 1;
                padding: 12px;
                border: none;
                border-radius: 8px;
                font-weight: 600;
                cursor: pointer;
                transition: background-color 0.2s ease;
                
                &.cancel-btn {
                    background: #F2F2F7;
                    color: #1C1C1E;
                    
                    &:hover {
                        background: #E5E5EA;
                    }
                }
                
                &.confirm-btn {
                    background: #007AFF;
                    color: white;
                    
                    &:hover:not(:disabled) {
                        background: #0056CC;
                    }
                    
                    &:disabled {
                        background: #C7C7CC;
                        cursor: not-allowed;
                    }
                }
            }
        }
    }
}

// Dark mode support
@media (prefers-color-scheme: dark) {
    .webphone-dialer {
        .call-status-display {
            .idle-display .number-input {
                color: white;
                
                &:focus {
                    background: rgba(0, 122, 255, 0.1);
                }
            }
            
            .calling-display, .ringing-display, .connected-display {
                .caller-number {
                    color: white;
                }
            }
            
            .incoming-display .caller-info .caller-details .caller-name {
                color: white;
            }
        }
        
        .keypad .keypad-row .keypad-key {
            background: #2C2C2E;
            
            &:hover {
                background: #3A3A3C;
            }
            
            &:active {
                background: #48484A;
            }
            
            .key-primary {
                color: white;
            }
        }
    }
    
    .transfer-modal .transfer-content {
        background: #1C1C1E;
        
        .transfer-header {
            border-bottom-color: #2C2C2E;
            
            h4 {
                color: white;
            }
        }
        
        .transfer-mode-tabs {
            border-bottom-color: #2C2C2E;
            
            .mode-tab {
                border-color: #3A3A3C;
                background: #2C2C2E;
                
                &:hover {
                    border-color: #007AFF;
                    background: rgba(0, 122, 255, 0.2);
                }
                
                &.active {
                    background: rgba(0, 122, 255, 0.3);
                }
            }
        }
        
        .manual-mode {
            .manual-input-container {
                .manual-input-label {
                    color: white;
                }
                
                .manual-number-input {
                    background: #2C2C2E;
                    border-color: #3A3A3C;
                    color: white;
                    
                    &:focus {
                        background: rgba(0, 122, 255, 0.1);
                    }
                    
                    &::placeholder {
                        color: #8E8E93;
                    }
                }
                
                .manual-input-hint {
                    background: #2C2C2E;
                    color: #8E8E93;
                }
            }
        }
        
        .search-box {
            border-bottom-color: #2C2C2E;
            
            .search-input {
                background: #2C2C2E;
                border-color: #3A3A3C;
                color: white;
                
                &::placeholder {
                    color: #8E8E93;
                }
            }
        }
        
        .extensions-list .extension-item {
            &:hover {
                background: rgba(255, 255, 255, 0.05);
            }
            
            &.selected {
                background: rgba(0, 122, 255, 0.2);
            }
            
            .extension-info .extension-name {
                color: white;
            }
        }
        
        .transfer-actions {
            border-top-color: #2C2C2E;
            
            .cancel-btn {
                background: #2C2C2E;
                color: white;
                
                &:hover {
                    background: #3A3A3C;
                }
            }
        }
    }
}
</style> 